import { Button } from '@progress/kendo-react-buttons';
import React from 'react';
import { AddOutline, SaveOutline } from '../../../../../icons/icons';
import { UdViewModel } from '../../../../../udView/udview';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { updateView } from '../../../../state/slices/views-slice';
import { store } from '../../../../state/store';
import { useCanSaveView, useFocusedWorkspaceSelector } from '../../hooks/use-view';
import { FlyoutActions, useFlyoutHeader } from '../Flyout';
import { UdViewEdit } from './UdViewEdit';
import { UdViewsList } from './UdViewsList';

export default function UdViewsFlyout() {
    const [isEdit, setIsEdit] = React.useState(false);
    const [editItem, setEditItem] = React.useState<UdViewModel | null>(null);
    const focusedWorkspaceId = useAppSelector((x) => x.workspace.selectedId);
    const selectedViewId = useFocusedWorkspaceSelector((x) => x.view.id);
    const defaultViewSelected = useFocusedWorkspaceSelector((x) => !x.view.id);
    const busy = useAppSelector((x) => x.views.busy);
    const dispatch = useAppDispatch();

    // To avoid any weird interactions, when the focused workspace item changes forcefully exit edit mode
    React.useEffect(() => {
        setEditItem(null);
        setIsEdit(false);
    }, [focusedWorkspaceId]);

    const context = useFlyoutHeader();

    React.useEffect(() => {
        context.setSubMenu(isEdit ? { title: 'Create a new view', onBack: () => setIsEdit(false) } : null);
    }, [isEdit]);

    const handleViewSave = React.useCallback(() => {
        const workspace = store.getState().workspace;
        const view = workspace.items[workspace.selectedId].view;
        dispatch(updateView({ view, workspaceId: focusedWorkspaceId }));
    }, [selectedViewId, focusedWorkspaceId]);

    const canSaveView = useCanSaveView();

    return (
        <div className="overflow-auto" style={{ minWidth: 300 }}>
            <FlyoutActions>
                {!isEdit && (
                    <>
                        <div id="ud-views-header-portal"></div>
                        <Button
                            id="view-save-btn"
                            title="Save"
                            className="button-small"
                            fillMode={'flat'}
                            disabled={defaultViewSelected || !canSaveView || busy}
                            svgIcon={SaveOutline}
                            onClick={handleViewSave}
                        />
                        <Button
                            id="add-view-btn"
                            title="Create a new view"
                            className="button-small"
                            fillMode={'flat'}
                            disabled={busy}
                            svgIcon={AddOutline}
                            onClick={() => {
                                setIsEdit(true);
                                setEditItem(null);
                            }}
                        />
                    </>
                )}
            </FlyoutActions>
            {!isEdit && (
                <UdViewsList
                    onEdit={(view) => {
                        setIsEdit(true);
                        setEditItem(view);
                    }}
                />
            )}

            {isEdit && <UdViewEdit edit={editItem} onClose={() => setIsEdit(false)} />}
        </div>
    );
}
